import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoSectionResponse } from '@/lib/api';
import { flattenSectionTree } from '../lib/sectionTree';

interface CreateSectionDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: string;
    description?: string;
    parentSectionId?: string;
    sectionType: 'category';
  }) => void;
  sections: TodoSectionResponse[];
}

export function CreateSectionDialog({
  open,
  onClose,
  onCreate,
  sections,
}: CreateSectionDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentSectionId, setParentSectionId] = useState<string>('none');

  // Parent options in the list's own tree order, with nesting depth for indenting.
  const parentOptions = useMemo(
    () =>
      flattenSectionTree(
        sections.filter((s) => s.sectionType === 'timeframe' || s.sectionType === 'category'),
      ),
    [sections],
  );

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    onCreate({
      name: trimmedName,
      description: description.trim() || undefined,
      parentSectionId: parentSectionId !== 'none' ? parentSectionId : undefined,
      sectionType: 'category',
    });

    setName('');
    setDescription('');
    setParentSectionId('none');
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Section</DialogTitle>
          <DialogDescription>Add a new section to organize items in this list.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="section-name">Name</Label>
            <Input
              id="section-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., High Priority, Groceries"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="section-description">Description (optional)</Label>
            <Textarea
              id="section-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What goes in this section?"
              rows={2}
            />
          </div>

          {parentOptions.length > 0 && (
            <div className="space-y-2">
              <Label>Parent Section</Label>
              <Select value={parentSectionId} onValueChange={setParentSectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="None (top-level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {parentOptions.map(({ section, depth }) => (
                    <SelectItem key={section.sectionId} value={section.sectionId}>
                      <span style={{ paddingLeft: `${depth}rem` }}>{section.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
